import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Users } from "lucide-react";
import { SectionCard } from "@/components/gooseberry-output/SectionCard";
import { SectionFeedbackBar } from "@/components/gooseberry-output/SectionFeedbackBar";

function cleanUserBody(body) {
  return (body || "")
    .replace(/\s+-\s*$/g, "")
    .trim();
}

export function SyntheticUsersPanel({ users = [], fallback, feedbackContext, language = "en" }) {
  const nl = language === "nl";
  if (!users || users.length === 0) {
    if (fallback) {
      return (
        <div data-testid="synberry-output-users-fallback">
          <div className="rounded-2xl border border-black/10 bg-[rgba(97,203,163,0.06)] p-3 mb-3 text-sm text-black/70">
            {nl
              ? "We konden de reactie niet opsplitsen in losse synthetische gebruikers. De sectie wordt daarom in zijn geheel getoond."
              : "We could not split the response into individual synthetic users. Showing the section as-is."}
          </div>
          <SectionCard section={fallback} feedbackContext={feedbackContext} language={language} />
        </div>
      );
    }
    return (
      <div className="rounded-2xl border border-dashed border-black/15 bg-white p-6 text-sm text-black/60" data-testid="synberry-output-users-empty">
        {nl ? "Er zijn geen synthetische gebruikers gevonden in deze output." : "No synthetic users were detected in the output."}
      </div>
    );
  }
  return (
    <div data-testid="synberry-output-users-grid-wrapper">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="synberry-output-users-grid">
        {users.map((u, i) => (
          <article
            key={`${u.name}-${i}`}
            className="rounded-2xl border border-black/10 bg-white p-5"
            data-testid="synberry-output-user-card"
          >
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[rgba(97,203,163,0.18)] text-[#61CBA3]">
                <Users className="h-4.5 w-4.5" />
              </span>
              <div>
                <div className="font-display font-semibold text-black">
                  {u.name.replace(/\s*\([^)]*\)/, "").trim()}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-black/50">{nl ? "Synthetische gebruiker" : "Synthetic user"}</div>
              </div>
            </div>
            <div className="mt-2 text-sm text-black/60 leading-relaxed">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ node, ...props }) => <p {...props} className="mb-2 last:mb-0" />,
                  strong: ({ node, ...props }) => <strong {...props} className="font-semibold text-black/80" />,
                }}
              >
                {cleanUserBody(u.body) || u.name.match(/\(([^)]+)\)/)?.[1] || (nl ? "Geen profiel beschikbaar" : "No profile available")}
              </ReactMarkdown>
            </div>
          </article>
        ))}
      </div>
      {feedbackContext?.runId && (
        <SectionFeedbackBar
          runId={feedbackContext.runId}
          sectionKey="synthetic users"
          sectionTitle={nl ? "Synthetische gebruikers" : "Synthetic users"}
          projectName={feedbackContext.projectName}
          mode={feedbackContext.mode}
          existing={feedbackContext.getFeedbackFor?.("synthetic users") || null}
          onSubmitted={feedbackContext.onSubmitted}
          language={language}
        />
      )}
    </div>
  );
}
